import type { ArchivedConversation } from '@crowdex/agent-contracts/api';
import {
  AudioLines,
  Box,
  Bug,
  CalendarDays,
  ChartColumn,
  Clock,
  Image,
  type LucideIcon,
  Sheet,
  UserRound,
  Video,
} from 'lucide-react';

/*
 * What `/desktop-preview` shows in the sidebar and in search & commands, as the boards do: it
 * lists no plugins, artifacts, files nor archive of its own. Only the preview reads it.
 */

export type PreviewApp = {
  view: string;
  title: string;
  description: string;
  icon: LucideIcon;
  disabled?: boolean;
};

export const PREVIEW_APPS: readonly PreviewApp[] = [
  { view: 'preview:design', title: 'Design', description: 'Pages, decks and posters', icon: Image },
  { view: 'preview:video', title: 'Video', description: 'Films the agent edits', icon: Video },
  {
    view: 'preview:live',
    title: 'Live',
    description: 'Voice calls with the assistant',
    icon: AudioLines,
  },
  {
    view: 'preview:planner',
    title: 'Planner',
    description: 'Plans and calendars',
    icon: CalendarDays,
  },
  {
    view: 'preview:automations',
    title: 'Automations',
    description: 'Runs on a schedule or event',
    icon: Clock,
  },
  {
    view: 'preview:dashboards',
    title: 'Dashboards',
    description: 'Charts the agent keeps up to date',
    icon: ChartColumn,
  },
  {
    view: 'preview:sheets',
    title: 'Sheets',
    description: 'Spreadsheets the agent can fill',
    icon: Sheet,
  },
  {
    view: 'preview:avatars',
    title: 'Avatars',
    description: 'A face for Live calls',
    icon: UserRound,
  },
  { view: 'preview:crowbox', title: 'Crowbox', description: 'Packages and sandboxes', icon: Box },
  { view: 'preview:debug', title: 'Debug', description: 'Disabled', icon: Bug, disabled: true },
];

/** The default pins of a preview (Design, Video), as the boards show them. */
export const PREVIEW_DEFAULT_PINS = ['preview:design', 'preview:video'];

/** The account a preview must reconnect (Settings › Connections "1 to reconnect", mobile 2). */
export const PREVIEW_ACCOUNTS_TO_RECONNECT: readonly string[] = ['GitHub'];

export const PREVIEW_SEARCH_ARTIFACTS = [
  { id: 'preview-artifact-ci', title: 'CI duration with cache', detail: 'Dashboard · crowdex' },
] as const;

export const PREVIEW_SEARCH_FILES = [
  '.github/workflows/ci.yml',
  'Dockerfile',
  'apps/web/src/components/mobile-shell/mobile-shell.tsx',
];

/** What the preview's messages say, for search by text. */
export const PREVIEW_SEARCH_MESSAGES = [
  {
    id: 'preview-ci',
    snippet: '…le cache du store pnpm fait gagner un tiers du temps…',
  },
];

export const PREVIEW_ARCHIVED: readonly ArchivedConversation[] = [
  {
    id: '3e9a1f85-9dbf-4f92-8d84-5c9f4b3ad044',
    title: 'Docker build lent',
    updatedAt: '2026-10-20T09:00:00.000Z',
  },
  {
    id: '4fab2096-aec0-4aa3-9e95-6dab5c4be155',
    title: 'Service worker',
    updatedAt: '2026-10-12T09:00:00.000Z',
  },
];
